/**
 * Single shared preview player for the whole V2 UI.
 *
 * Routing every preview through this module guarantees at most ONE clip plays at
 * a time, supports play/stop toggling, and lets components reflect the live
 * playing state (subscribe via `onPreviewChange`).
 *
 * `preloadPreview()` warms a clip ahead of time (e.g. the plan's selected voice)
 * so the first click plays INSTANTLY instead of waiting on a network fetch.
 */
let current: HTMLAudioElement | null = null;
let currentUrl: string | null = null;
let loadingUrl: string | null = null; // clicked + buffering, before playback starts
const warm = new Map<string, HTMLAudioElement>();
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

/** Subscribe to play/stop/end/loading transitions. Returns an unsubscribe fn. */
export function onPreviewChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Is this exact url currently playing? */
export function isPreviewPlaying(url?: string | null): boolean {
  return !!url && currentUrl === url && !!current && !current.paused;
}

/** Clicked + buffering, but playback hasn't actually started yet. */
export function isPreviewLoading(url?: string | null): boolean {
  return !!url && loadingUrl === url;
}

/** Buffer a clip now so a later playPreview(url) starts immediately. */
export function preloadPreview(url?: string | null): void {
  if (!url || warm.has(url)) return;
  try {
    const a = new Audio();
    a.preload = 'auto';
    a.src = url;
    a.load();
    warm.set(url, a);
  } catch { /* ignore */ }
}

export function playPreview(url?: string | null): void {
  stopPreview();
  if (!url) return;
  // Reuse the preloaded (already-buffered) element when we have one.
  const pre = warm.get(url);
  current = pre ?? new Audio(url);
  if (!pre) current.preload = 'auto';
  currentUrl = url;
  loadingUrl = url; // spinner until playback actually starts
  current.onended = () => {
    if (currentUrl === url) { current = null; currentUrl = null; loadingUrl = null; notify(); }
  };
  // 'playing' fires the instant real playback begins → swap spinner → stop icon.
  current.onplaying = () => { if (loadingUrl === url) { loadingUrl = null; notify(); } };
  try { current.currentTime = 0; } catch { /* not seekable yet */ }
  void current.play().catch(() => { current = null; currentUrl = null; loadingUrl = null; notify(); });
  notify();
}

export function stopPreview(): void {
  if (current) {
    current.onended = null;
    current.onplaying = null;
    current.pause();
    // Keep warm clips intact for instant replay; only drop the live ref.
    current = null;
  }
  currentUrl = null;
  loadingUrl = null;
  notify();
}

/** Play if this url isn't already playing; otherwise stop. */
export function togglePreview(url?: string | null): void {
  if (isPreviewPlaying(url)) stopPreview();
  else playPreview(url);
}
